import { Ingest } from "@pagerless/schema";
import type { ReactNode } from "react";
import { Link } from "react-router";
import { Pager } from "../components/Lcd";
import { Wordmark } from "../components/Wordmark";

/** The product's own domain (pagerless.dev), from the SDKs' default events host. */
const PRODUCT_DOMAIN = new URL(Ingest.baseUrl).host.replace(/^[^.]+\./, "");

/**
 * Where a link into the app goes. On the marketing site (the bare product domain) the app lives at
 * app.<domain>, so it's a full link there; anywhere else (app host, workers.dev, local) it's in-app.
 */
export function appHref(path: string, host: string): string | null {
  return host === PRODUCT_DOMAIN ? `https://app.${PRODUCT_DOMAIN}${path}` : null;
}

function AppLink({ to, className, children }: { to: string; className?: string; children: ReactNode }) {
  const href = appHref(to, window.location.host);
  return href ? (
    <a href={href} className={className}>
      {children}
    </a>
  ) : (
    <Link to={to} className={className}>
      {children}
    </Link>
  );
}

export function TopBar({ minimal }: { minimal?: boolean }) {
  return (
    <header className="topbar">
      <Link to="/" className="logo">
        <Wordmark />
      </Link>
      {!minimal && (
        <nav aria-label="Site">
          <a href="#how" className="hide-sm">
            How it works
          </a>
          <a href="#pricing" className="hide-sm">
            Pricing
          </a>
          <AppLink to="/login">Log in</AppLink>
          <AppLink to="/signup" className="btn sm">
            Start free
          </AppLink>
        </nav>
      )}
    </header>
  );
}

export function Landing() {
  return (
    <>
      <title>Pagerless: your app has a night shift now</title>
      <TopBar />
      <main id="main">
        <section className="land" aria-labelledby="hero">
          <div>
            <p className="eyebrow">For anyone who built an app with AI</p>
            <h1 id="hero" className="h1">
              Your app has a night shift now.
            </h1>
            <p className="lede">
              Pagerless watches the app you built. Your AI helper fixes what it can. We only wake you when it really
              needs you, and then it takes one tap.
            </p>
            <div className="btn-row">
              <AppLink to="/signup" className="btn hot">
                Start free
              </AppLink>
              <a href="#night" className="btn alt">
                See a night at 3 a.m.
              </a>
            </div>
            <p className="note">Free for you and one backup. No card.</p>
          </div>
          <Pager lines={["THIS WEEK", "HELPER FIXED 213", "YOU WERE NEEDED 1", "> 9 SECONDS"]} />
        </section>

        <section id="how" className="steps3" aria-label="How it works">
          <div>
            <span className="num" aria-hidden="true">
              1
            </span>
            <b>Add it</b>
            <p>Ask your AI to "add Pagerless", or paste one line.</p>
          </div>
          <div>
            <span className="num" aria-hidden="true">
              2
            </span>
            <b>Pick who to wake</b>
            <p>You, and someone to call if you don't answer.</p>
          </div>
          <div>
            <span className="num" aria-hidden="true">
              3
            </span>
            <b>Go to sleep</b>
            <p>Small problems get fixed. Big ones get you, with the fix ready.</p>
          </div>
        </section>
        <p className="works">
          Works with apps on Vercel · Railway · Render · Cloudflare · Supabase · anything that runs code
        </p>

        <section id="night" className="night" aria-labelledby="night-h">
          <div className="night-in">
            <Pager brand="1996" lines={["CALL OFFICE", "555-0142", "URGENT"]} />
            <div>
              <h2 id="night-h" className="h2" style={{ color: "#fbf9f2" }}>
                3:10 a.m., Thursday
              </h2>
              <p>
                In 1996 a pager would only have said "call the office". Pagerless says what broke, why, and what to do.
                One tap, Face ID, back to sleep. Fixed in 6 minutes; you spent 9 seconds.
              </p>
            </div>
            <Pager lines={["PAYMENTS DOWN", "CAUSE: 3:08 UPDATE", "UNDO IT? [YES]"]} hotKey />
          </div>
        </section>

        <section id="pricing" className="setup" aria-labelledby="pricing-h">
          <h2 id="pricing-h" className="h2">
            Pricing
          </h2>
          <div className="two">
            <div className="box planc">
              <b>Free</b>
              <p className="muted">You + 1 backup · 3 apps · unlimited fixes</p>
              <AppLink to="/signup" className="btn sm">
                Start free
              </AppLink>
            </div>
            <div className="box planc">
              <b>Team</b>
              <p className="muted">More people · pay per alert we send · no per-seat price</p>
              <AppLink to="/signup" className="btn alt sm">
                Start free, upgrade later
              </AppLink>
            </div>
          </div>
          <p className="note">Phone calls use your own Twilio account, so we never mark them up.</p>
        </section>
      </main>
    </>
  );
}
